'use client';

import { fetchUser, getAccessToken, refreshToken, signIn, signOut } from '@/lib/auth-client';
import { jwtDecode } from 'jwt-decode';
import { usePathname } from 'next/navigation';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  user: any | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<boolean>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const pathname = usePathname();

  // Add check for public routes
  const isPublicRoute = ['/avm', '/integration-test'].some(route => pathname?.includes(route));

  const [user, setUser] = useState<any | null>(() => {
    if (typeof window !== 'undefined') {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    }
    return null;
  });

  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
      setIsAuthenticated(true);
    } else {
      localStorage.removeItem('user');
      setIsAuthenticated(false);
    }
  }, [user]);

  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Skip auth check for public routes
        if (isPublicRoute) {
          setIsLoading(false);
          return;
        }

        const accessToken = await getAccessToken();
        
        if (accessToken) {
          // Fetch user data using the access token
          const userResponse = await fetchUser();
          
          if (userResponse.success && userResponse.authenticated) {
            const userData = userResponse.user;
            
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            setIsAuthenticated(true);
          } else {
            setUser(null);
            localStorage.removeItem('user');
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          localStorage.removeItem('user');
          setIsAuthenticated(false);
        }
      } catch (error: any) {
        console.error('Auth check error:', error);
        setError(error.message || 'Failed to check authentication status');
        setUser(null);
        localStorage.removeItem('user');
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Set up a refresh interval (every 10 minutes)
    const refreshInterval = setInterval(async () => {
      const idToken = localStorage.getItem('idToken');
      if (idToken) {
        try {
          const decoded = jwtDecode<{ exp: number }>(idToken);
          const currentTime = Math.floor(Date.now() / 1000);
          
          // Refresh if token will expire in the next 15 minutes
          if (decoded.exp - currentTime < 15 * 60) {
            await refreshToken();
          }
        } catch (error: any) {
          console.error('Token refresh interval error:', error);
          setError(error.message || 'Failed to refresh authentication token');
        }
      }
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(refreshInterval);
  }, [isPublicRoute]);

  const login = async (email: string, password: string) => {
    setError(null);
    try {
      const response = await signIn(email, password);
      
      if (response.success) {
        // Fetch user data after successful login
        const userResponse = await fetchUser();
        
        if (userResponse.success && userResponse.authenticated) {
          const userData = userResponse.user;
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          setIsAuthenticated(true);
          return { success: true };
        } else {
          const errorMessage = userResponse.error || 'Failed to fetch user data';
          setError(errorMessage);
          return { success: false, error: errorMessage };
        }
      } else {
        const errorMessage = response.error || 'Authentication failed';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      const errorMessage = error.message || 'An unexpected error occurred';
      console.error('Login error:', error);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    setError(null);
    try {
      await signOut();
    } catch (error: any) {
      console.error('Logout API error:', error);
      setError(error.message || 'Failed to sign out');
    } finally {
      // Update state and clear storage
      setUser(null);
      localStorage.removeItem('user');
      setIsAuthenticated(false);
    }
  };

  // Manual refresh function
  const refreshAuth = async () => {
    setError(null);
    try {
      return await refreshToken();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to refresh authentication';
      setError(errorMessage);
      return false;
    }
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        error,
        user,
        login,
        logout,
        refreshAuth,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}